'use client';

import { useTranslations } from 'next-intl';
import React from 'react';

import HorizontalMovieList from '@/components/HorizontalMovieList';
import ListPlaceholderSimple from '@/components/list/ListPlaceholderSimple';

import { CachedContentData } from '@/hooks/useCachedContentData';
import { useVirtualPagination } from '@/hooks/useVirtualPagination';

import CenterLayout from '@cineamo/composables/src/CenterLayout';

import { Link } from '@/i18n/routing';
import { useDomainConfiguration } from '@/providers/DomainConfigurationProvider';

type InCinemaTabPanelProps = {
    moviesInCinemas?: CachedContentData[];
    onSelectedContentChange: (current: CachedContentData, next: CachedContentData, isFading: boolean) => void;
    isAutoScrollingEnabled: boolean;
    isEnabled: boolean;
    onPointerDown: () => void;
};

const InCinemaTabPanel: React.FC<InCinemaTabPanelProps> = ({
    moviesInCinemas,
    onSelectedContentChange,
    isAutoScrollingEnabled,
    isEnabled,
    onPointerDown
}) => {
    const domainConfiguration = useDomainConfiguration();
    const t = useTranslations();

    const { visibleCount, loadMoreRef, hasMore } = useVirtualPagination({
        items: moviesInCinemas,
        config: {
            initialLoad: 10,
            loadMore: 10
        }
    });

    const contentsList = moviesInCinemas?.slice(0, visibleCount);
    const emptyData = contentsList?.length === 0;

    return (
        <CenterLayout className="flex flex-col">
            {emptyData ? (
                <ListPlaceholderSimple text={t('home.currently-no-screenings-in-cinemas')} />
            ) : (
                <>
                    <HorizontalMovieList
                        contents={contentsList}
                        onSelectedContentChange={onSelectedContentChange}
                        paginationLoaderRef={loadMoreRef}
                        paginationIsFetching={false}
                        paginationHasNextPage={hasMore}
                        isAutoScrollingEnabled={isAutoScrollingEnabled}
                        isEnabled={isEnabled}
                        onPointerDown={onPointerDown}
                    />
                    <Link
                        className="mt-[24px] py-6 px-12 text-body-bold-14 rounded-4 self-start"
                        href="/program?date=all"
                        style={{
                            backgroundColor: domainConfiguration.style.surfaceColor,
                            color: domainConfiguration.style.onSurfaceColor
                        }}>
                        {t('home.view-all')}
                    </Link>
                </>
            )}
        </CenterLayout>
    );
};

export default InCinemaTabPanel;
